import { useMutation } from '@tanstack/react-query';
import { AxiosRequestConfig } from 'axios';
import { useState } from 'react';
import { useOrganizationContext } from 'src/sections/@dashboard/organization/hooks/useOrganizationContext';
import { warehouseErrorCode } from '../utils/errors';
import {
  requireOrganizationId,
  warehouseRequestOptions,
} from '../utils/request';
import {
  operationKeyScope,
  scopedOperationKeys,
  useOperationKey,
} from './useOperationKey';

/** A write through the patient's endpoints, with the visit, if any, in the body. */
export interface PatientWriteRoute {
  via: 'patient';
  profileId: string;
  visitId: string | null;
}

/** A write through the visit's own endpoints; the server resolves the patient. */
export interface VisitWriteRoute {
  via: 'visit';
  /**
   * The visit's registered patient, null on a guest visit. It only keys a
   * create's series; the endpoint never reads it.
   */
  profileId: string | null;
  visitId: string;
}

/**
 * Where a clinical write goes besides its target (F-4). It holds two separate
 * things:
 * - `profileId` + `visitId` name the patient and the visit. A create has no
 *   entry id yet, so its series is keyed by them (see `seriesScope`): every
 *   screen of one patient and visit shares one create series, whichever
 *   endpoint it writes through. Pass the registered patient whenever it is
 *   known, and null only on a guest visit.
 * - `via` names the endpoint. It is frozen with the first attempt, and a
 *   `send` reads it from the frozen request alone: the backend hashes the
 *   route into the operation key, so a resend always goes to the endpoint of
 *   its first attempt, whichever screen resends it.
 * Every screen keeps its own endpoint: `patientOrVisitRoute` for a screen
 * that writes through the patient when there is one; an explicit
 * `{ via: 'visit', profileId: <patient> | null, visitId }` for one that
 * always writes through the visit. Task 24's treatment series follow the same
 * rule.
 */
export type ClinicalWriteRoute = PatientWriteRoute | VisitWriteRoute;

/**
 * The route of a screen that writes through the patient's endpoints when the
 * visit has a registered patient, and through the visit's own endpoints on a
 * guest visit (`profileId` empty or null).
 */
export function patientOrVisitRoute(
  profileId: string | null | undefined,
  visitId: string | null | undefined,
): ClinicalWriteRoute {
  return profileId
    ? { via: 'patient', profileId, visitId: visitId || null }
    : { via: 'visit', profileId: null, visitId: visitId ?? '' };
}

/**
 * R3-4: one frozen request of a clinical write series. F-4: it carries the
 * organization and the route of its first attempt, so a resend never follows
 * a clinic switch or the screen the form now sits on.
 */
export interface ClinicalWriteRequest<TTarget, TBody, TValues = unknown> {
  organizationId: string;
  route: ClinicalWriteRoute;
  /** `null` for a create, the entry id for an update. */
  target: TTarget;
  body: TBody & { operationKey: string };
  /** The form values the body was built from; a reopened form shows them. */
  values?: TValues;
}

export interface ClinicalWriteSeries<TTarget, TBody, TValues = unknown> {
  /**
   * Sends a new request, or resends the frozen one while the target's series
   * is unresolved. A host submits the target it shows (the hook's `target`):
   * `pending`, `blocked`, `lastError` and `frozenValues` describe that entry
   * alone, so a form showing one entry and saving another would read the
   * wrong state. Outside production a mismatch is reported on the console.
   */
  submit: (target: TTarget, body: TBody, values?: TValues) => void;
  isLoading: boolean;
  /** The shown target's last attempt may have committed unseen: the form resends, it does not start anew. */
  pending: boolean;
  /** F-8: the shown target's series is held by 422 WAREHOUSE_DISABLED. */
  blocked: boolean;
  /** The shown target's last failed attempt; cleared by its success. */
  lastError: unknown;
  /** While pending: the values of the frozen request, for a reopened form. */
  frozenValues: TValues | undefined;
}

export interface ClinicalWriteOptions<TTarget, TBody, TResponse, TValues> {
  /** The write kind, e.g. `clinical-medication`; the first part of each series scope. */
  operation: string;
  /**
   * The entry the form shows now: its id for an edit, `null` (never `''`)
   * for a create.
   */
  target: TTarget;
  /** The screen's patient, visit and endpoint; frozen with the first attempt. */
  route: ClinicalWriteRoute;
  /** `request` is what was actually sent: the frozen request of the series. */
  onSuccess: (
    response: TResponse,
    request: ClinicalWriteRequest<TTarget, TBody, TValues>,
  ) => void;
  onError?: (error: unknown) => void;
}

interface Failure {
  scope: string;
  error: unknown;
}

/**
 * The series scope of `target`: `<operation>:<org>:<entry id>` for an edit,
 * `<operation>:<org>:new:<profileId|->:<visitId|->` for a create. A create is
 * keyed by its patient and visit (notes-2, Tasks 22/24), never by `via`, so
 * an unresolved one survives the form host's unmount, and a second create on
 * the same patient and visit, from any screen, resends it instead of posting
 * again. `undefined` when no scope can be built (no organization, or an empty
 * target).
 */
function seriesScope(
  operation: string,
  organizationId: string | undefined,
  target: string | null,
  route: ClinicalWriteRoute,
): string | undefined {
  const entry =
    target === null
      ? `new:${route.profileId || '-'}:${route.visitId || '-'}`
      : target;
  return operationKeyScope(operation, organizationId, entry);
}

/**
 * R3-4: every Facility medication and treatment create or update goes
 * through here, with or without stock. One series per organization and
 * target (notes-2 F-4, see `seriesScope`): Phase 1's `useOperationKey`
 * registry keeps the key and the frozen request after every ambiguous
 * outcome, also across an unmount, and while it does the form's current
 * state is never sent. `send` must build the call from the request alone,
 * choosing the endpoint by `request.route.via` (see `ClinicalWriteRoute`),
 * and pass `options` (the explicit `x-organization-id` of the series) to the
 * generated method.
 */
export function useClinicalWriteSeries<
  TTarget extends string | null,
  TBody extends object,
  TResponse,
  TValues = unknown,
>(
  send: (
    request: ClinicalWriteRequest<TTarget, TBody, TValues>,
    options: AxiosRequestConfig,
  ) => Promise<TResponse>,
  {
    operation,
    target: shownTarget,
    route,
    onSuccess,
    onError,
  }: ClinicalWriteOptions<TTarget, TBody, TResponse, TValues>,
): ClinicalWriteSeries<TTarget, TBody, TValues> {
  type Request = ClinicalWriteRequest<TTarget, TBody, TValues>;
  const selectedOrganization =
    useOrganizationContext().selectedOrganization?.id;
  const shownScope = seriesScope(
    operation,
    selectedOrganization,
    shownTarget,
    route,
  );
  const shown = useOperationKey(shownScope);
  const [failure, setFailure] = useState<Failure | null>(null);
  const { mutateAsync, isLoading } = useMutation<TResponse, unknown, Request>({
    mutationFn: (request) =>
      send(request, warehouseRequestOptions(request.organizationId)),
  });

  const submit = (target: TTarget, body: TBody, values?: TValues): void => {
    if (process.env.NODE_ENV !== 'production' && target !== shownTarget) {
      const [sent, shown] = [String(target), String(shownTarget)];
      console.error(
        `useClinicalWriteSeries(${operation}): submit(${sent}) is not the ` +
          `shown target ${shown}; pending and blocked describe the shown one.`,
      );
    }
    let organizationId: string;
    try {
      // Refused before a series exists: a non-HTTP error inside one would
      // keep it pending for good.
      organizationId = requireOrganizationId(selectedOrganization);
    } catch (error) {
      onError?.(error);
      return;
    }
    const scope = seriesScope(operation, organizationId, target, route);
    if (scope === undefined) {
      // An empty target would share one registry entry with every other.
      onError?.(new Error('Clinical write without a target'));
      return;
    }
    const keys = scopedOperationKeys(scope);
    // This attempt's key: its answer settles only its own series.
    const key = keys.current();
    const request = keys.request<Request>(() => ({
      organizationId,
      route,
      target,
      body: { ...body, operationKey: key },
      values,
    }));
    mutateAsync(request).then(
      (response) => {
        keys.settle(undefined, key);
        setFailure((last) => (last?.scope === scope ? null : last));
        onSuccess(response, request);
      },
      (error: unknown) => {
        keys.settle(error, key);
        setFailure({ scope, error });
        onError?.(error);
      },
    );
  };

  const lastError =
    failure && failure.scope === shownScope ? failure.error : undefined;
  return {
    submit,
    isLoading,
    pending: shown.pending,
    blocked:
      shown.pending && warehouseErrorCode(lastError) === 'WAREHOUSE_DISABLED',
    lastError,
    frozenValues: shown.pending ? shown.peek<Request>()?.values : undefined,
  };
}
